import {useSelector} from 'react-redux'
import {skipToken} from '@reduxjs/toolkit/query'
import {differenceInDays} from 'date-fns/differenceInDays'
import {ulid} from 'ulid'

import {SIZES} from '@vio/atlas/Image'
import {imageProvider} from '@vio/atlas/testutils/imageProvider'
import {Rate} from '@vio/core/src/analytics/services/CustomerIo/types'
import {
  getCognitoUserTier,
  selectUserId
} from '@vio/core/src/auth/modules/selectors'
import {getTimezone} from '@vio/core/src/utils/date'
import {logger} from '@vio/core/src/utils/logging/logger'

import {
  useCancelPriceWatchMutation,
  useCreateAnonymousPriceWatchTrackingMutation,
  useGetUserPriceWatchTrackingsQuery
} from '../services'
import {selectPriceWatchEntry} from '../slice/selectors'
import {
  InitPriceWatchOptions,
  PriceWatchFlow,
  PriceWatchMetric,
  PriceWatchRoom
} from '../types'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

export interface UsePriceWatchProps {
  checkIn: string
  checkOut: string
  rooms: string
  currency: string
  locale: string
  hotel: Hotel | ContentHotel
  rate: Rate
  onPriceWatchInitialized?: (options?: InitPriceWatchOptions) => void
  userCountry: string
  device: string
  room?: PriceWatchRoom
  bookingId?: string
  /** Serialized with serializeCancellationPenalties. */
  cancellationPenalties?: string[]
  isFreeCancellation?: boolean
  freeCancellationUntil?: string
}

export const usePriceWatch = ({
  checkIn,
  checkOut,
  rooms,
  currency,
  locale,
  hotel,
  rate,
  userCountry,
  device,
  onPriceWatchInitialized,
  room,
  bookingId,
  cancellationPenalties,
  isFreeCancellation,
  freeCancellationUntil
}: UsePriceWatchProps) => {
  const userId = useSelector(selectUserId)

  const nights = differenceInDays(new Date(checkOut), new Date(checkIn))
  const hotelId = hotel?.objectID

  const {
    data: userPriceWatchTrackings = [],
    isSuccess: userPriceWatchTrackingsLoaded
  } = useGetUserPriceWatchTrackingsQuery(userId ? {userId} : skipToken)

  const priceWatchEntry = useSelector(
    selectPriceWatchEntry({checkIn, checkOut, rooms, hotelId, currency, userId})
  )

  const priceWatchId = priceWatchEntry?.priceWatchId
  const isEnabled = priceWatchEntry?.metric !== PriceWatchMetric.Paused
  const isPriceWatchOn = Boolean(priceWatchId) && isEnabled

  const hotelImageUrl = hotel?.imageURIs?.[0]
    ? imageProvider(hotel?.imageURIs[0], SIZES['large'])
    : undefined

  const [cancelPriceWatch] = useCancelPriceWatchMutation()
  const [createPriceWatch] = useCreateAnonymousPriceWatchTrackingMutation()

  const tiers = getCognitoUserTier()

  const initPriceWatch = async (options?: InitPriceWatchOptions) => {
    if (!userId) return

    const priceWatchId = ulid()
    try {
      onPriceWatchInitialized?.(options)
      await createPriceWatch({
        priceWatchId,
        userId,
        checkIn,
        checkOut,
        hotelId,
        rooms,
        userCountry,
        device,
        source: 'frontend',
        flow: PriceWatchFlow.EXPLICIT,
        nights,
        tiers,
        hotel: {
          name: hotel?.hotelName || '',
          starRating: hotel?.starRating || 0,
          image: hotelImageUrl || ''
        },
        currency,
        locale,
        rate: {
          taxesIncluded: Boolean(rate.taxesIncluded),
          localTaxesIncluded: Boolean(rate.localTaxesIncluded),
          base: rate.base,
          hotelFees: rate.hotelFees,
          taxes: rate.taxes
        },
        timezone: getTimezone(),
        ...(room && {room}),
        ...(bookingId && {bookingId}),
        ...(cancellationPenalties?.length && {cancellationPenalties}),
        ...(isFreeCancellation !== undefined && {isFreeCancellation}),
        ...(freeCancellationUntil && {freeCancellationUntil})
      }).unwrap()
    } catch (error) {
      logger.error('Error creating price watch:', error)
    }
  }

  const handleCancelPriceWatch = async ({
    remove = false,
    priceWatchId: priceWatchIdOverride
  }: {remove?: boolean; priceWatchId?: string} = {}) => {
    const queryParams = new URLSearchParams(location.search)
    const urlPriceWatchId = queryParams.get('priceWatchId')
    const priceWatchIdWithFallback =
      priceWatchIdOverride || priceWatchId || urlPriceWatchId
    if (!priceWatchIdWithFallback) throw new Error('No priceWatchId found')
    if (!userId) return

    try {
      await cancelPriceWatch({
        priceWatchId: priceWatchIdWithFallback,
        userId,
        remove
      }).unwrap()
    } catch (error) {
      logger.error('Error canceling PriceWatch', error)
    }
  }

  return {
    priceWatchEntry,
    isPriceWatchOn,
    initPriceWatch,
    cancelPriceWatch: handleCancelPriceWatch,
    userPriceWatchTrackings,
    userPriceWatchTrackingsLoaded
  }
}
